import { useEffect, useState, useMemo } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { CoursesNav, CoursesList } from '@/components/course';
import { courseService } from '@/services/courseService';
import { Course } from '@/types/course';
import { Certification } from '@/types/certification';
import { useRefresh } from '@/contexts/RefreshContext';
import { useProfileStore } from '@/store/useProfileStore';
import { isTeamOwnerOrAdmin } from '@/types/profile';

export function AllCoursesPage() {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCertificationId, setSelectedCertificationId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  const { activeTeam, activeProfile } = useProfileStore();
  const isOwnerOrAdmin = activeTeam && activeProfile && isTeamOwnerOrAdmin(activeProfile);
  const activeTrialCertificationIds = activeProfile?.userPlan?.trial?.filter(t => t.status === 'active').map(t => t.certificationId) || [];

  const fetchData = async () => {
    try {
      setLoading(true);
      const certificationsData = await courseService.getCertificationsWithCourses();

      // Reorder certifications: active trial certifications first
      let orderedCertifications = certificationsData;
      if (activeTrialCertificationIds.length > 0) {
        const withActiveTrial = certificationsData.filter(cert => activeTrialCertificationIds.includes(cert.id));
        const withoutActiveTrial = certificationsData.filter(cert => !activeTrialCertificationIds.includes(cert.id));
        orderedCertifications = [...withActiveTrial, ...withoutActiveTrial];
      }

      setCertifications(orderedCertifications);

      // Initialize selectedCertificationId to first certification if not set
      if (orderedCertifications.length > 0 && !selectedCertificationId) {
        setSelectedCertificationId(orderedCertifications[0].id);
      }
    } catch (error) {
      // Error modal presented by API
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await fetchData();
  };

  const handleSelect = (certificationId: string, category: string) => {
    setSelectedCertificationId(certificationId);
    setSelectedCategory(category);
  };

  // Refetch data when navigating to this page
  useEffect(() => {
    fetchData();
  }, [location.pathname]);

  // Register refresh handler for Test Panel
  useEffect(() => {
    registerRefreshHandler('/all-courses', handleRefresh);
    return () => {
      unregisterRefreshHandler('/all-courses');
    };
  }, [registerRefreshHandler, unregisterRefreshHandler]);

  const filteredAndSortedCourses = useMemo(() => {
    // Find the selected certification
    const selectedCert = certifications.find(c => c.id === selectedCertificationId);
    if (!selectedCert) return [];

    // Get courses from the selected certification
    let coursesFromCert: Course[];
    if (selectedCategory === 'all') {
      // Get all courses from all categories in this certification
      coursesFromCert = selectedCert.courseCategories.flatMap(cat => cat.courses);
    } else {
      // Get courses from specific category
      const category = selectedCert.courseCategories.find(cat => cat.category === selectedCategory);
      coursesFromCert = category?.courses || [];
    }

    // Sort courses
    let sorted: Course[];
    if (selectedCategory === 'all') {
      // Sort by CourseCategory.order, then Course.categoryOrder
      const categoryOrderMap = new Map<string, number>();
      selectedCert.courseCategories.forEach((cat) => {
        categoryOrderMap.set(cat.category, cat.order);
      });

      sorted = [...coursesFromCert].sort((a, b) => {
        const categoryOrderA = categoryOrderMap.get(a.category) ?? 999;
        const categoryOrderB = categoryOrderMap.get(b.category) ?? 999;

        if (categoryOrderA !== categoryOrderB) {
          return categoryOrderA - categoryOrderB;
        }

        return a.categoryOrder - b.categoryOrder;
      });
    } else {
      // Sort by Course.categoryOrder only
      sorted = [...coursesFromCert].sort((a, b) => a.categoryOrder - b.categoryOrder);
    }

    // Apply disable logic
    if (activeProfile) {
      const hasActiveTrial = activeTrialCertificationIds.includes(selectedCertificationId || '');

      if ((!activeTeam && !hasActiveTrial) || (activeTeam && isOwnerOrAdmin && !hasActiveTrial)) {
        // Disable all courses if no active trial or team owner/admin without trial
        sorted = sorted.map(course => ({ ...course, disabled: true }));
      }
    }

    return sorted;
  }, [certifications, selectedCertificationId, selectedCategory, activeTeam, activeProfile]);

  if (loading) {
    return (
      <IonPage>
        <IonContent className="bg-gray-50">
          <div className="flex h-full items-center justify-center">
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent className="bg-gray-50">
        <div className="flex h-full">
          <CoursesNav
            certifications={certifications}
            selectedCertificationId={selectedCertificationId}
            selectedCategory={selectedCategory}
            onSelect={handleSelect}
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
          <CoursesList
            courses={filteredAndSortedCourses}
            onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
            selectedCategory={selectedCategory}
            onRefresh={handleRefresh}
          />
        </div>
      </IonContent>
    </IonPage>
  );
}

